import { apiBlog } from "@/api/blog.api.ts";
import { FileItem } from "@/components/files/FileItem.tsx";
import i18n from "@/i18n.ts";
import { BlogAttachmentPreviewModal } from "@/pages/blog/components/BlogAttachmentPreviewModal.tsx";
import { isBlogAttachmentPreviewable } from "@/types/blog.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { Button, Stack, Title, useModals, useToasts } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface BlogPostAttachmentsProps {
  postId: number;
  files: ApiFile[];
  onRemove?: (file: ApiFile) => void;
  isFileOpenDisabled?: (file: ApiFile) => boolean;
  showTitle?: boolean;
}

const VISIBLE_ATTACHMENTS_COUNT = 3;

export function BlogPostAttachments({
  postId,
  files,
  onRemove,
  isFileOpenDisabled,
  showTitle = true,
}: BlogPostAttachmentsProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { showError } = useToasts();
  const [isShowAll, setIsShowAll] = useState(false);

  if (files.length === 0) return null;

  const visibleFiles = isShowAll ? files : files.slice(0, VISIBLE_ATTACHMENTS_COUNT);
  const hiddenFilesCount = files.length - visibleFiles.length;

  const openAttachment = async (file: ApiFile) => {
    if (isBlogAttachmentPreviewable(file)) {
      openModal(BlogAttachmentPreviewModal, { postId, file });
      return;
    }

    const { data } = await apiBlog.getAttachmentLink(postId, file.uuid);
    if (!data?.download_url) {
      showError({ title: i18n.t("api_errors:blog_attachment_not_found") });
      return;
    }
    window.open(data.download_url, "_blank", "noopener,noreferrer")?.focus();
  };

  return (
    <Stack className={"w-full"}>
      {showTitle && <Title header={t("blog:attachments")} size="xs" paddingBottom paddingTop />}
      <Stack direction="horizontal" gap="sm" className="flex-wrap">
        {visibleFiles.map((file) => (
          <FileItem
            key={file.uuid}
            file={file}
            className="min-w-0 w-fit max-w-full"
            nameClassName="min-w-0 flex-1"
            showMimeType={false}
            isOpenDisabled={postId <= 0 || isFileOpenDisabled?.(file)}
            onOpenFile={openAttachment}
            onRemove={onRemove ? () => onRemove(file) : undefined}
            removeTitle={t("blog:attachment_remove")}
          />
        ))}
        {hiddenFilesCount > 0 && (
          <Button variant="secondary" onClick={() => setIsShowAll(true)}>
            {t("common:show_more_template", { count: hiddenFilesCount })}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
